/*
 *  Copyright (c) 2022-2023 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { useMemo } from "react";
import { ApolloError } from "@apollo/client";
import { ParentSize } from "@visx/responsive";
import { Else, If, Then } from "react-if";
import { theme } from "@nordcloud/gnui";
import {
  BrickLoader,
  UniversalWrap,
  mapAccumulatedCostChartColors,
  StackBarChart,
  ChartType,
  CostAnalysisFields,
  Granularity,
  Period,
  EstatePeriod,
  Wrapper,
  DateRange,
  LoaderWrap,
} from "~/components";
import { useCurrency } from "~/hooks";
import { getEstateTotalsTableColumns, EstateCostTable } from "../Views";
import { EstateTotals } from "./EstateTotals";
import { getAverageCost } from "./helpers";
import { useEstateRecordsAccumulatedCost } from "./hooks";
import { ChartJobData, EstateGroupBy } from "./types";

type Props = {
  startDate: string;
  endDate: string;
  granularity: Granularity;
  period: EstatePeriod;
  loading: boolean;
  isCostLoading: boolean;
  estateRecordsCount: number;
  range?: DateRange;
  chartData?: ChartJobData;
  error?: ApolloError;
  currentMonthTotal?: string;
  monthlySavings?: string | null;
};

const ACCUMULATED_COST_KEYS = [
  CostAnalysisFields.DAILY_COST,
  CostAnalysisFields.CORRECTION_COST,
  CostAnalysisFields.SAVINGS_PLANS,
];

export function EstateRecordsAccumulatedCostChart({
  startDate,
  endDate,
  granularity,
  period,
  range,
  loading,
  chartData,
  error,
  isCostLoading,
  estateRecordsCount,
  currentMonthTotal,
  monthlySavings,
}: Props) {
  const { currency } = useCurrency();

  const {
    accumulatedCostChartData,
    accumulatedCostTableData,
    accumulatedCostTotal,
  } = useEstateRecordsAccumulatedCost(granularity, chartData);

  const colors = mapAccumulatedCostChartColors(ACCUMULATED_COST_KEYS);

  const columns = useMemo(
    () =>
      getEstateTotalsTableColumns({
        startDate,
        endDate,
        currency,
        data: accumulatedCostTableData,
        period,
        colors,
        chartType: ChartType.ACCUMULATED_COST,
      }),
    [startDate, endDate, currency, accumulatedCostTableData, period, colors]
  );

  return (
    <Wrapper
      css={{ margin: theme.spacing.spacing02 }}
      data-testid="accumulated-cost-chart-wrapper"
    >
      <UniversalWrap
        loaderProps={{ loading, Component: BrickLoader }}
        errorProps={{ error }}
      >
        <>
          <ParentSize>
            {(parent) => (
              <StackBarChart
                width={parent.width}
                height={280}
                data={accumulatedCostChartData}
                granularity={granularity}
                loading={false}
                startDate={startDate}
                endDate={endDate}
                period={Period.MONTH}
                keys={ACCUMULATED_COST_KEYS}
                colors={colors}
                chartType={ChartType.ACCUMULATED_COST}
              />
            )}
          </ParentSize>
        </>
      </UniversalWrap>
      <If condition={period === EstatePeriod.RANGE}>
        <Then>
          <EstateTotals
            period={period}
            range={range}
            isCostLoading={loading}
            totalCost={accumulatedCostTotal}
            chartType={EstateGroupBy.NONE}
            averageCost={getAverageCost(
              accumulatedCostTotal,
              startDate,
              endDate
            )}
          />
          <div css={{ width: "100%", overflow: "scroll" }}>
            <LoaderWrap loading={loading} Component={BrickLoader}>
              <EstateCostTable
                data={accumulatedCostTableData}
                columns={columns}
                period={EstatePeriod.CURRENT_MONTH}
              />
            </LoaderWrap>
          </div>
        </Then>
        <Else>
          <EstateTotals
            period={period}
            range={range}
            isCostLoading={isCostLoading}
            totalCost={currentMonthTotal}
            count={estateRecordsCount}
            monthlySavings={monthlySavings}
            chartType={EstateGroupBy.NONE}
          />
        </Else>
      </If>
    </Wrapper>
  );
}
